import { getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { deserializeCurvaPackage, serializeCurvaPackage } from "./package";

const DATABASE_NAME = "curva-editor";
/**
 * Bumped when object stores are added.
 * v2 intended `editor-scripts`; some sessions already sat at v2 without that store,
 * so v3 forces `onupgradeneeded` to create it.
 */
const DATABASE_VERSION = 3;
const DOCUMENT_STORE = "documents";
export const EDITOR_SCRIPTS_STORE = "editor-scripts";
const CURRENT_DOCUMENT_KEY = "current";
export const EDITOR_SCRIPTS_RECORD_KEY = "current";

interface PersistedDocumentRecord {
  id: typeof CURRENT_DOCUMENT_KEY;
  bytes: ArrayBuffer;
  savedAt: string;
}

export interface PersistedEditorScriptsRecord {
  id: typeof EDITOR_SCRIPTS_RECORD_KEY;
  bytes: ArrayBuffer;
  savedAt: string;
}

export interface PersistedDocumentInspection {
  savedAt?: string;
  byteLength: number;
  id: string;
  name: string;
  revision: number;
  pageCount: number;
  nodeCount: number;
  countsByType: Record<string, number>;
  effectCounts: Record<string, number>;
  largestBlurs: Array<{ nodeId: string; name: string; type: string; blur: number }>;
  topLevel: Array<{ id: string; name: string; type: string; childCount?: number; bounds: SceneNode["bounds"] }>;
}

export function isSafeMode(): boolean {
  if (typeof globalThis.location === "undefined") {
    return false;
  }
  const params = new URLSearchParams(globalThis.location.search);
  if (!params.has("safe")) {
    return false;
  }
  const value = params.get("safe");
  return value === null || value === "" || value === "1" || value === "true";
}

export async function loadPersistedDocument(): Promise<CurvaDocument | undefined> {
  if (!isIndexedDbAvailable()) {
    return undefined;
  }

  const database = await openPersistenceDatabase();
  try {
    const transaction = database.transaction(DOCUMENT_STORE, "readonly");
    const record = await requestToPromise<PersistedDocumentRecord | undefined>(
      transaction.objectStore(DOCUMENT_STORE).get(CURRENT_DOCUMENT_KEY),
    );
    if (!record) {
      return undefined;
    }

    return deserializeCurvaPackage(new Uint8Array(record.bytes)).document;
  } finally {
    database.close();
  }
}

export async function savePersistedDocument(document: CurvaDocument): Promise<void> {
  if (!isIndexedDbAvailable()) {
    return;
  }
  if (isSafeMode()) {
    console.info("[curva] safe mode: persistence writes are disabled");
    return;
  }

  const bytes = serializeCurvaPackage(document);
  const record: PersistedDocumentRecord = {
    id: CURRENT_DOCUMENT_KEY,
    bytes: copyToArrayBuffer(bytes),
    savedAt: new Date().toISOString(),
  };

  const database = await openPersistenceDatabase();
  try {
    const transaction = database.transaction(DOCUMENT_STORE, "readwrite");
    const store = transaction.objectStore(DOCUMENT_STORE);
    await Promise.all([
      requestToPromise(store.put(record)),
      transactionDone(transaction),
    ]);
  } finally {
    database.close();
  }
}

/** Read the raw IndexedDB record without applying it to the editor. */
export async function readPersistedDocumentRecord(): Promise<PersistedDocumentRecord | undefined> {
  if (!isIndexedDbAvailable()) {
    return undefined;
  }
  const database = await openPersistenceDatabase();
  try {
    const transaction = database.transaction(DOCUMENT_STORE, "readonly");
    return await requestToPromise<PersistedDocumentRecord | undefined>(
      transaction.objectStore(DOCUMENT_STORE).get(CURRENT_DOCUMENT_KEY),
    );
  } finally {
    database.close();
  }
}

export async function inspectPersistedDocument(): Promise<PersistedDocumentInspection | undefined> {
  const record = await readPersistedDocumentRecord();
  if (!record) {
    return undefined;
  }
  const document = deserializeCurvaPackage(new Uint8Array(record.bytes)).document;
  return summarizeDocument(document, record);
}

export function summarizeDocument(
  document: CurvaDocument,
  record?: Pick<PersistedDocumentRecord, "savedAt" | "bytes">,
): PersistedDocumentInspection {
  const nodes = getAllSceneNodes(document);
  const countsByType: Record<string, number> = {};
  const effectCounts: Record<string, number> = {};
  const largestBlurs: PersistedDocumentInspection["largestBlurs"] = [];

  for (const node of nodes) {
    countsByType[node.type] = (countsByType[node.type] ?? 0) + 1;
    for (const effect of node.style.effects ?? []) {
      effectCounts[effect.type] = (effectCounts[effect.type] ?? 0) + 1;
      if (effect.type === "layerBlur" || effect.type === "backgroundBlur" || effect.type === "dropShadow" || effect.type === "innerShadow") {
        const blur = "blur" in effect ? effect.blur : 0;
        largestBlurs.push({ nodeId: node.id, name: node.name, type: effect.type, blur });
      }
    }
  }

  largestBlurs.sort((a, b) => b.blur - a.blur);

  const page = document.pages[0];
  return {
    savedAt: record?.savedAt,
    byteLength: record?.bytes.byteLength ?? 0,
    id: document.id,
    name: document.name,
    revision: document.revision,
    pageCount: document.pages.length,
    nodeCount: nodes.length,
    countsByType,
    effectCounts,
    largestBlurs: largestBlurs.slice(0, 20),
    topLevel: (page?.children ?? []).map(node => ({
      id: node.id,
      name: node.name,
      type: node.type,
      childCount: "children" in node && Array.isArray(node.children) ? node.children.length : undefined,
      bounds: node.bounds,
    })),
  };
}

export async function downloadPersistedDocument(filename = "curva-recovered.curva"): Promise<boolean> {
  const record = await readPersistedDocumentRecord();
  if (!record) {
    console.warn("[curva] no persisted document found");
    return false;
  }
  const blob = new Blob([record.bytes], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  console.info(`[curva] downloaded ${filename} (${record.bytes.byteLength} bytes, savedAt=${record.savedAt})`);
  return true;
}

export async function exportPersistedDocumentBase64(): Promise<string | undefined> {
  const record = await readPersistedDocumentRecord();
  if (!record) {
    return undefined;
  }
  const bytes = new Uint8Array(record.bytes);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

/** Installs `window.curvaPersistence` for recovery from the browser console. */
export function installPersistedDocumentConsoleApi(): void {
  if (typeof window === "undefined") {
    return;
  }
  const api = {
    isSafeMode: isSafeMode(),
    inspect: inspectPersistedDocument,
    download: downloadPersistedDocument,
    exportBase64: exportPersistedDocumentBase64,
    load: loadPersistedDocument,
    help: () => {
      console.info(
        [
          "curvaPersistence helpers:",
          "  await curvaPersistence.inspect()     // summarize stored doc without applying it",
          "  await curvaPersistence.download()    // download raw .curva bytes",
          "  await curvaPersistence.exportBase64()// base64 of stored package",
          "  await curvaPersistence.load()        // deserialize only (does not apply to editor)",
          "Boot with ?safe to skip restore and disable persistence writes.",
        ].join("\n"),
      );
    },
  };
  (window as Window & { curvaPersistence?: typeof api }).curvaPersistence = api;
  if (isSafeMode()) {
    console.info("[curva] safe mode: restored document skipped; persistence writes disabled. Run curvaPersistence.help()");
  }
}

function openPersistenceDatabaseAtVersion(version: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(DATABASE_NAME, version);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DOCUMENT_STORE)) {
        database.createObjectStore(DOCUMENT_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(EDITOR_SCRIPTS_STORE)) {
        database.createObjectStore(EDITOR_SCRIPTS_STORE, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open document persistence database."));
    request.onblocked = () => {
      console.warn("[curva] IndexedDB open blocked; close other Curva tabs and reload");
    };
  });
}

async function openPersistenceDatabase(): Promise<IDBDatabase> {
  const database = await openPersistenceDatabaseAtVersion(DATABASE_VERSION);
  const missingDocumentStore = !database.objectStoreNames.contains(DOCUMENT_STORE);
  const missingEditorScriptsStore = !database.objectStoreNames.contains(EDITOR_SCRIPTS_STORE);
  if (!missingDocumentStore && !missingEditorScriptsStore) {
    return database;
  }

  // DB already at DATABASE_VERSION without required stores (skipped upgrade) — force another bump.
  const repairVersion = Math.max(database.version + 1, DATABASE_VERSION + 1);
  database.close();
  return openPersistenceDatabaseAtVersion(repairVersion);
}

/** Shared IndexedDB open for editor-scripts store (same DB as document persistence). */
export async function openCurvaEditorDatabase(): Promise<IDBDatabase> {
  return openPersistenceDatabase();
}

export function copyBytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return copyToArrayBuffer(bytes);
}

export function idbRequestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return requestToPromise(request);
}

export function idbTransactionDone(transaction: IDBTransaction): Promise<void> {
  return transactionDone(transaction);
}

export function isIndexedDbAvailable(): boolean {
  return typeof globalThis.indexedDB !== "undefined";
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted."));
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed."));
  });
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
